import BarList from '@/components/ui/BarList.tsx';
import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
} from '@/components/ui/InfoCard.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { CHART_PALETTE } from '@/components/ui/chartPalette.ts';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { resolveSegmentIdentity } from '@/components/charts/breakdownAdapter.ts';
import { BreakdownDto } from '@/types/stats/BreakdownDto.ts';
import { StatSegmentRegistry } from '@/types/stats/StatSegmentRegistry.ts';
import { AGE_BUCKET_REGISTRY } from '@/features/dashboard/tours/types/sales/ageBuckets.utils.ts';
import { CITY_BUCKET_REGISTRY } from '@/features/dashboard/tours/types/sales/cities.utils.ts';
import { barShare } from '@/features/dashboard/tours/types/share.utils.ts';

interface DemographicsBlockProps {
  title: string;
  breakdown: BreakdownDto;
  registry: StatSegmentRegistry;
  emptyTitle: string;
  caption?: string;
}

/**
 * Jedno zestawienie demograficzne. Mianownik bierze się z `total` rozbicia,
 * a nie z sumy segmentów: wiek liczy się względem uczestników aktywnych,
 * miasta — względem tych, którzy w ogóle mają miasto.
 */
const DemographicsBlock = ({
  title,
  breakdown,
  registry,
  emptyTitle,
  caption,
}: DemographicsBlockProps) => (
  <div className="flex flex-col gap-3">
    <span className={columnLabelClass}>{title}</span>
    {breakdown.segments.length > 0 ? (
      <>
        <BarList
          layout="inline"
          color={CHART_PALETTE.reference}
          rows={breakdown.segments.map((segment, index) => {
            const { id, label } = resolveSegmentIdentity(
              segment,
              registry,
              index,
            );

            return {
              id,
              label,
              value: segment.value,
              pct: barShare(segment.value, breakdown.total),
            };
          })}
        />
        {caption && <p className="text-[13px] text-neutral-400">{caption}</p>}
      </>
    ) : (
      <NoItems title={emptyTitle} />
    )}
  </div>
);

interface DemographicsCardProps {
  age: BreakdownDto;
  cities: BreakdownDto;
}

/** Kto jedzie: przedziały wieku i miasta, z których dojeżdżają uczestnicy. */
const DemographicsCard = ({ age, cities }: DemographicsCardProps) => (
  <InfoCard>
    <InfoCardHeader title="Uczestnicy" />
    <InfoCardBody>
      <div className="grid gap-6 md:grid-cols-2">
        <DemographicsBlock
          title="Wiek"
          breakdown={age}
          registry={AGE_BUCKET_REGISTRY}
          emptyTitle="Brak danych o wieku"
        />
        <DemographicsBlock
          title="Skąd dojeżdżają"
          breakdown={cities}
          registry={CITY_BUCKET_REGISTRY}
          emptyTitle="Brak danych o miastach"
          caption="Miasta zapisujących, pisownia bez ujednolicania — „Kraków” i „Krakow” to dwa wiersze."
        />
      </div>
    </InfoCardBody>
  </InfoCard>
);

export default DemographicsCard;
